import { apiClient, ApiError } from '@/core/infra_api_client'

/**
 * Thrown when incident data cannot be retrieved.
 * A 404, 501 or 503 response does not establish backend deployment state.
 */
export class IncidentsUnavailableError extends Error {
  constructor(endpoint: string) {
    super(`Data unavailable: ${endpoint}`)
    this.name = 'IncidentsUnavailableError'
  }
}

function isUnavailable(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 404 || error.status === 501 || error.status === 503)
}

// ============= Types =============

export type IncidentStatus = 'open' | 'investigating' | 'identified' | 'resolved' | 'closed'
export type IncidentSeverity = 'critical' | 'major' | 'minor' | 'info'

export interface Incident {
  id: string
  title: string
  description: string
  status: IncidentStatus
  severity: IncidentSeverity
  environmentId?: string
  affectedServices: string[]
  assignee?: string
  createdAt: string
  updatedAt: string
  resolvedAt?: string
}

export interface IncidentTimelineEvent {
  id: string
  incidentId: string
  type: string
  message: string
  author: string
  createdAt: string
  metadata?: Record<string, unknown>
}

export interface PaginatedResponse<T> {
  data: T[]
  total: number
  page: number
  perPage: number
  totalPages: number
}

// ============= API =============

const BASE_PATH = 'incidents'

export async function listIncidents(params?: {
  page?: number
  perPage?: number
  status?: string
  severity?: string
  environmentId?: string
}): Promise<PaginatedResponse<Incident>> {
  const searchParams: Record<string, string | number | boolean> = {}
  if (params?.page) searchParams['page'] = params.page
  if (params?.perPage) searchParams['per_page'] = params.perPage
  if (params?.status) searchParams['status'] = params.status
  if (params?.severity) searchParams['severity'] = params.severity
  if (params?.environmentId) searchParams['environment_id'] = params.environmentId

  try {
    return await apiClient.get<PaginatedResponse<Incident>>(BASE_PATH, searchParams)
  } catch (error) {
    if (isUnavailable(error)) throw new IncidentsUnavailableError(`/v1/infra/${BASE_PATH}`)
    throw error
  }
}

export async function getIncident(id: string): Promise<Incident> {
  try {
    return await apiClient.get<Incident>(`${BASE_PATH}/${id}`)
  } catch (error) {
    if (isUnavailable(error)) throw new IncidentsUnavailableError(`/v1/infra/${BASE_PATH}/${id}`)
    throw error
  }
}

export async function listTimelineEvents(incidentId: string, params?: {
  page?: number
  perPage?: number
}): Promise<PaginatedResponse<IncidentTimelineEvent>> {
  const searchParams: Record<string, string | number | boolean> = {}
  if (params?.page) searchParams['page'] = params.page
  if (params?.perPage) searchParams['per_page'] = params.perPage

  try {
    return await apiClient.get<PaginatedResponse<IncidentTimelineEvent>>(
      `${BASE_PATH}/${incidentId}/timeline`,
      searchParams
    )
  } catch (error) {
    if (isUnavailable(error)) throw new IncidentsUnavailableError(`/v1/infra/${BASE_PATH}/${incidentId}/timeline`)
    throw error
  }
}
